import {
  AppPrice,
  Fee,
  InstancePrice,
  PriceDisplayOverride,
  FeeAmountType,
  PricingRule,
} from '@vendasta/marketplace-apps';
import { billingFrequencyToUnitPriceFrequency, labelForGalaxyFrequency, toGalaxyFrequency } from './frequency';

// Considers only single frequency
export function getFrequencyValueForEdition(input: AppPrice, currencyCode: string, editionId?: string): string {
  if (input.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES) {
    return;
  }

  if (editionId === undefined) {
    editionId = getLowestEditionId(input, currencyCode);
  }

  // This was added to handle editioned private products, which were not being gated during this workaround
  if (!input.pricesForEditions?.[editionId] || !input.pricesForEditions?.[editionId].pricesForCurrencies) {
    return;
  }

  // This was added to prevent issues where prices don't exist for certain currencies
  if (!input.pricesForEditions?.[editionId].pricesForCurrencies?.[currencyCode]) {
    return;
  }

  const pricingFrequency =
    input.pricesForEditions[editionId].pricesForCurrencies[currencyCode].pricesForFrequencies[0].frequency;
  const galaxyFrequency = toGalaxyFrequency(pricingFrequency);
  return labelForGalaxyFrequency(galaxyFrequency, true);
}

export function getFeesForEdition(input: AppPrice, currencyCode: string, editionId?: string): Fee[] {
  if (input.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES) {
    return;
  }

  if (!input.pricesForEditions?.[editionId]?.pricesForCurrencies) {
    return [];
  }

  if (editionId === undefined) {
    editionId = getLowestEditionId(input, currencyCode);
  }

  if (!input.pricesForEditions[editionId].pricesForCurrencies[currencyCode]) {
    return [];
  }

  const freqPrice = input.pricesForEditions[editionId].pricesForCurrencies[currencyCode].pricesForFrequencies[0];
  return freqPrice.fees;
}

export function getSetupFee(input: AppPrice, currencyCode: string, editionId?: string): number {
  if (input.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES) {
    return 0;
  }

  if (!input.pricesForEditions?.[editionId]?.pricesForCurrencies) {
    return 0;
  }

  if (editionId === undefined) {
    editionId = getLowestEditionId(input, currencyCode);
  }

  if (!input.pricesForEditions[editionId].pricesForCurrencies[currencyCode]) {
    return 0;
  }

  const freqPrice = input.pricesForEditions[editionId].pricesForCurrencies[currencyCode].pricesForFrequencies[0];
  return freqPrice?.setupFee || 0;
}

export function calculateFeeAmount(cost: number, fees: Fee[]): number {
  let feeAmount = 0;
  fees.forEach((fee) => {
    if (fee.amountType === FeeAmountType.FEE_AMOUNT_TYPE_PERCENTAGE) {
      feeAmount += cost * (fee.amount / 100);
    } else {
      feeAmount += fee.amount;
    }
  });
  return Math.round(feeAmount);
}

export function getPriceValueForEdition(
  input: AppPrice,
  currencyCode: string,
  editionId?: string,
  instance?: boolean,
): number {
  if (input.displayOverride === PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES) {
    return;
  }

  if (!input.pricesForEditions?.[editionId]?.pricesForCurrencies) {
    return 0;
  }

  if (editionId === undefined) {
    editionId = getLowestEditionId(input, currencyCode);
  }

  if (!input.pricesForEditions[editionId].pricesForCurrencies[currencyCode]) {
    return 0;
  }

  const freqPrice = input.pricesForEditions[editionId].pricesForCurrencies[currencyCode].pricesForFrequencies[0];

  if (instance) {
    return freqPrice.instancePrice?.total - (freqPrice.setupFee || 0);
  } else {
    return freqPrice.pricingRules[0].price || 0;
  }
}

// Used to update the value based on quantites (calculating total)
export function setAppPrice(input: AppPrice, currencyCode: string, newValue: number, editionId?: string): AppPrice {
  if (editionId === undefined) {
    editionId = getLowestEditionId(input, currencyCode);
  }
  if (!input.pricesForEditions?.[editionId] || !input.pricesForEditions?.[editionId]?.pricesForCurrencies) {
    return input;
  }

  // Copy object losing the reference to all its children attributes
  const clonedAppPrice = JSON.parse(JSON.stringify(input)) as AppPrice;

  const freqPrice =
    clonedAppPrice.pricesForEditions[editionId].pricesForCurrencies[currencyCode].pricesForFrequencies[0];

  freqPrice.pricingRules[0].price = newValue;

  return clonedAppPrice;
}

// Used to change the custom spend of a variable priced product
// Or to update the value based on quantites (calculating total)
export function setInstanceAppPrice(
  input: AppPrice,
  currencyCode: string,
  newValue: number,
  editionId?: string,
): AppPrice {
  if (editionId === undefined) {
    editionId = getLowestEditionId(input, currencyCode);
  }
  if (!input.pricesForEditions?.[editionId] || !input.pricesForEditions?.[editionId]?.pricesForCurrencies) {
    return input;
  }

  // Copy object losing the reference to all its children attributes
  const clonedAppPrice = JSON.parse(JSON.stringify(input)) as AppPrice;

  const freqPrice =
    clonedAppPrice.pricesForEditions[editionId].pricesForCurrencies[currencyCode].pricesForFrequencies[0];

  const setupFee = freqPrice.setupFee || 0;

  if (freqPrice.instancePrice) {
    freqPrice.instancePrice.total = newValue + setupFee;
  } else {
    freqPrice.instancePrice = new InstancePrice({
      total: newValue + setupFee,
    });
  }

  return clonedAppPrice;
}

export function getSetupFeeForEdition(input: AppPrice, currencyCode: string, editionId?: string): number {
  if (editionId === undefined) {
    editionId = getLowestEditionId(input, currencyCode);
  }

  if (!input.pricesForEditions?.[editionId]?.pricesForCurrencies?.[currencyCode]) {
    return 0;
  }

  const freqPrice = input.pricesForEditions[editionId].pricesForCurrencies[currencyCode].pricesForFrequencies[0];

  return freqPrice?.setupFee || 0;
}

export function getLowestEditionId(input: AppPrice, currencyCode: string): string {
  let lowestEditionValue = -2;
  let editionToBeUsed = '';
  if (!input?.pricesForEditions) {
    return editionToBeUsed;
  }
  Object.keys(input.pricesForEditions).forEach((key) => {
    // This was added to handle bad data in demo. I fixed the bad data, but decided to leave it here just in case
    if (!input.pricesForEditions[key] || !input.pricesForEditions[key].pricesForCurrencies) {
      return;
    }

    const priceForCurrency = input.pricesForEditions[key].pricesForCurrencies[currencyCode];

    if (!priceForCurrency) {
      return;
    }

    const freqPrices = priceForCurrency.pricesForFrequencies;

    const editionValue = freqPrices[0].pricingRules[0].price || 0;

    if (lowestEditionValue === -2 || lowestEditionValue > editionValue) {
      lowestEditionValue = editionValue;
      editionToBeUsed = key;
    }
  });

  return editionToBeUsed;
}

export function getPartnerCurrency(appPrice: AppPrice): string {
  let partnerCurrency = '';
  //get partner currency
  if (appPrice?.pricesForEditions !== undefined) {
    const firstEdition = Object.keys(appPrice.pricesForEditions)[0];
    if (appPrice?.pricesForEditions?.[firstEdition]?.pricesForCurrencies !== undefined) {
      const firstEditionCurrency = Object.keys(appPrice.pricesForEditions[firstEdition].pricesForCurrencies)?.[0];
      if (firstEditionCurrency !== undefined) {
        partnerCurrency = firstEditionCurrency;
      }
    }
  }
  return partnerCurrency;
}

export function convertProductPricingToAppPrice(
  appId: string,
  editionId: string,
  usesEditions: boolean,
  usesCustomPricing: boolean,
  currency,
  productPricing,
): AppPrice {
  const unitPriceRules = productPricing.pricingRules.map((rule) => {
    return new PricingRule({
      price: rule.price,
      max: rule.maxUnits,
      min: rule.minUnits,
    });
  });

  let displayOverride = PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_NONE;

  unitPriceRules.forEach((rule) => {
    if (rule.price === -1) {
      displayOverride = PriceDisplayOverride.PRICE_DISPLAY_OVERRIDE_CONTACT_SALES;
    }
  });

  return {
    appId: appId,
    displayOverride,
    billingConfiguration: {
      usesEditions: usesEditions,
      usesVariablePricing: usesCustomPricing,
    },
    pricesForEditions: {
      [editionId]: {
        pricesForCurrencies: {
          [currency]: {
            pricesForFrequencies: [
              {
                frequency: billingFrequencyToUnitPriceFrequency(productPricing.frequency),
                pricingRules: unitPriceRules,
                setupFee: productPricing.setupFee,
                commitment: productPricing.commitment,
                fees: productPricing.fees,
              },
            ],
          },
        },
      },
    },
  } as AppPrice;
}
